import { httpPost, httpPatchMerge, httpDelete } from './http'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import { getContactsForTsdIdPreProcessed } from './tsds'
import type { INonEntityForPost, TEntityId, TOmitNamed } from '~/types/models'
import type { TContact } from '~/types/contact/models'
import type {
  IOpportunityRegistrationForPost,
  IOpportunityRegistration,
} from '~/types/opportunity/models'
import type { IEmailMessage } from '~/types/email/models'
import {
  addressedEmailForPost,
  emailForPostFromForm,
  type IEmailFormValues,
} from '~/cynqueModules/emailForPost'
import { OpportunityRegistration } from '~/cynqueModules/dataObjects/OpportunityRegistration'
import { Contact } from '~/cynqueModules/dataObjects/Contact'

const apiEntityPath = '/api/opportunityregistrations'

/**
 * Who a registration email can go to: the supplier's contacts plus, when the
 * registration goes through a TSD, that TSD's -- those with an address, sorted
 * by last name. The Email: To picker's items.
 *
 * ⚠ The `Contact()` re-run is deliberate, as in getContactsForCaseServicePreProcessed
 * (services/cases.ts): SupplierContact() / TsdContact() build a `namePlusBiz`
 * ending in "NaN" (a unary `+` on the phone), and Contact() rebuilds it as
 * "Name | parent | title", which is what the picker shows.
 *
 * An absent id contributes no contacts.
 */
export async function getOpportunityRegistrationRecipientContacts({
  supplierId,
  tsdId,
}: {
  supplierId?: TEntityId
  tsdId?: TEntityId
}): Promise<TContact[]> {
  const [supplierContacts, tsdContacts] = await Promise.all([
    supplierId
      ? getContactsForSupplierIdPreProcessed(supplierId)
      : Promise.resolve([]),
    tsdId ? getContactsForTsdIdPreProcessed(tsdId) : Promise.resolve([]),
  ])
  return [...supplierContacts, ...tsdContacts]
    .filter((contact) => !!contact.email)
    .map((contact) => Contact(contact))
    .sort((a, b) =>
      (a.person.name.lastPlusFirst ?? '') > (b.person.name.lastPlusFirst ?? '')
        ? 1
        : -1
    )
}

// The dialog's form-only keys: neither is a field of the registration.
// `registeredManually` is the "Registered manually (record only, no email)"
// box; `registrationEmail` is the email's fields, sent only by a create.
interface IRegistrationFormValues {
  registeredManually?: unknown
  registrationEmail?: unknown
}

/**
 * A create's request body: the registration's fields, plus EITHER the email the
 * form composed under `registrationEmail` as an IAddressedEmailForPost (each
 * recipient list split into addresses), OR -- "Registered manually" ticked --
 * its Registration Details. The other half was hidden, and is left out.
 */
export function opportunityRegistrationRequestBody<
  T extends IRegistrationFormValues & { registrationDetails?: unknown }
>(
  values: T
): TOmitNamed<T, keyof IRegistrationFormValues | 'registrationDetails'> & {
  registrationEmail?: IOpportunityRegistrationForPost['registrationEmail']
  registrationDetails?: T['registrationDetails']
} {
  const {
    registrationEmail,
    registeredManually,
    registrationDetails,
    ...rest
  } = values
  // The rest is Omit<T, ...>, which drops every named field of an entity
  // (TOmitNamed); TypeScript cannot relate the two for a generic T.
  const registration = rest as unknown as TOmitNamed<
    T,
    keyof IRegistrationFormValues | 'registrationDetails'
  >
  if (registeredManually) {
    return registrationDetails === undefined
      ? registration
      : { ...registration, registrationDetails }
  }
  if (!registrationEmail) {
    return registration
  }
  const email = emailForPostFromForm(registrationEmail as IEmailFormValues)
  if (!email.to?.length) {
    // The dialog's To is required while sending, so this is a caller that
    // skipped it -- caught here, as the server's 422 would read as an expired
    // session and log the user out.
    throw new Error(
      `${opportunityRegistrationRequestBody.name} | a registration email needs a To.`
    )
  }
  return {
    ...registration,
    registrationEmail: addressedEmailForPost({ ...email, to: email.to }),
  }
}

/**
 * The email and Registration Details the server says the registration has,
 * written onto the object the dialog sent -- IN PLACE -- and the form-only box
 * taken off it.
 *
 * ⚠ The save path keeps the object it sent as the card's row, copying back only
 * `hrid` and `version` (writeBackHridsAndVersions, entityWriteBack.ts). Left
 * alone, the row would hold the dialog's DRAFTS of the half it hid -- To / cc /
 * Message on a registration made manually, the Registration Details on one
 * that sent -- where the overview holds null. The email draft made the next
 * edit open as if sent.
 */
function writeBackRegistrationEmail(
  sent: IRegistrationFormValues & { registrationDetails?: unknown },
  response: unknown
) {
  const saved = response as Partial<IOpportunityRegistration> | undefined
  sent.registrationEmail = saved?.registrationEmail ?? null
  sent.registrationDetails = saved?.registrationDetails ?? null
  delete sent.registeredManually
}

/**
 * Create a registration, sending its email unless it was registered manually.
 *
 * Returns the registration as the overview carries it: `registrationEmail` is
 * the message the send produced -- `queued` until a worker has sent it, as a
 * case reply's -- or null when nothing was sent. The same is written onto
 * `entity` (writeBackRegistrationEmail).
 */
export async function createOpportunityRegistration(
  entity: IOpportunityRegistrationForPost & IRegistrationFormValues
) {
  const callingMn = createOpportunityRegistration.name
  const apiPath = apiEntityPath
  const created = (await httpPost<IOpportunityRegistrationForPost>({
    callingMn,
    apiPath,
    entity: opportunityRegistrationRequestBody(entity),
  })) as unknown as IOpportunityRegistration
  writeBackRegistrationEmail(entity, created)
  return created
}

/**
 * Change a registration's own fields. Never its email: that is sent once, by
 * the create, and an edit shows it read-only -- or, registered manually,
 * hidden. So neither form-only key goes on the PATCH, and both come off
 * `entityData` too (IN PLACE, as on a create), or the row would take a
 * hidden draft for its email.
 */
export async function updateOpportunityRegistration(
  entityData: Partial<IOpportunityRegistrationForPost> &
    IRegistrationFormValues,
  id: TEntityId
) {
  const callingMn = updateOpportunityRegistration.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialOpportunityRegistration = Partial<IOpportunityRegistration>
  delete entityData.registrationEmail
  delete entityData.registeredManually
  return httpPatchMerge<TPartialOpportunityRegistration>({
    callingMn,
    apiPath,
    entityData: entityData as TPartialOpportunityRegistration,
  })
}

/**
 * Send a registration's email again -- the row menu's Resend.
 *
 * An action, not an edit: POST /api/opportunityregistrations/{id}/resend, no
 * body. The server sends the stored email once more, to the same recipients
 * with the same subject and text, and answers with the NEW message: `queued`,
 * its `repeatOf` the original's id (IEmailMessage). The registration's
 * `registrationEmail` reads as that latest attempt from then on, so its
 * Delivery shows how the resend fared.
 *
 * Refused (409) for a registration that sent no email -- one registered
 * manually -- that is no longer Submitted (the supplier has answered), or
 * whose last attempt is still `queued` or `sending`: it has not gone out yet,
 * and resending it would send it twice. The row menu offers Resend only when
 * none of these holds.
 */
export async function resendOpportunityRegistrationEmail(id: TEntityId) {
  const callingMn = resendOpportunityRegistrationEmail.name
  const apiPath = `${apiEntityPath}/${id}/resend`
  return (await httpPost<INonEntityForPost>({
    callingMn,
    apiPath,
    entity: {},
  })) as unknown as IEmailMessage
}

export async function deleteOpportunityRegistration(
  entityData: IOpportunityRegistration
) {
  const callingMn = deleteOpportunityRegistration.name
  const apiPath = apiEntityPath
  return httpDelete<IOpportunityRegistration>({
    callingMn,
    apiPath,
    entityData,
  })
}

export function setUpOpportunityRegistration(
  rawOpportunityRegistration: IOpportunityRegistration
) {
  return OpportunityRegistration(rawOpportunityRegistration)
}
